import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import {
  signUpWithEmail,
  accountVerifyEmail,
  checkDuplicateUser,
  logout as logoutUser,
  GENERIC_ERROR,
} from '@/services/auth/authService';
import { getUserDetails, updateUserDetails } from '@/services/api/authApi';
import { candidateDetailsFromJson, candidateDetailsToJson } from '@/services/api/profileApi';
import { isHandledByErrorInterceptor } from '@/services/api/apiClient';
import { analyticsService } from '@/services/analytics/analyticsService';
import { ROUTES } from '@/app/router/routes';
import { resolvePostLoginRedirect, type LoginRedirectState } from '@/services/auth/postLoginRedirect';

/**
 * useSignup — orchestrates the full Flutter sign-up flow.
 *
 * Mirrors Flutter WebSignupBloc:
 *   1. Validate form fields
 *   2. checkDuplicateUser (POST /v1/p/candidate/checkDuplicity) — a failed check stops here
 *   3. createUserWithEmailAndPassword (Firebase)
 *   4. updateUserDetails (PUT /v1/candidate — CandidateDetails.toJson() in an array)
 *   5. accountVerifyEmail (POST /v1/account/verify/email)
 *   6. Show email verification card
 *   7. Poll Firebase for emailVerified status (only once the email was sent)
 *   8. On verified: GET v1/candidate, then the "Email Verified Successfully !" dialog
 *      (Flutter WebAccVerifiedDialog); closing it goes to /mfa (tenant MFA) or the
 *      ProfileInput destination
 *
 * The verify-email card is derived from the signed-in Firebase user, so it shows in
 * every case where the email is not verified yet: right after sign up, after signing
 * in with an unverified email, on a page refresh, and when AccountGuard sends an
 * unverified user here from another page. Flutter's signup page init re-sends the
 * verification email in those cases (signupEmailVerify), so it is sent once per visit.
 *
 * Every navigation logs the screen_view Flutter's NavigationService.navigateTo() logs.
 *
 * Flutter source files:
 *   - application/web_auth/web_signup/web_signup_bloc.dart
 *   - presentation/web/auth/web_signup/web_signup_page.dart
 */

export type SignUpPhase =
  | 'form'
  | 'verifyEmail';

export interface SignUpFormData {
  firstName: string;
  lastName: string;
  phone: string;
  countryCode: string;
  email: string;
  password: string;
  confirmPassword: string;
  referralCode: string;
  agreeToPolicy: boolean;
}

/** Flutter WebAccVerifiedDialog, shown once polling sees the email verified. */
export interface AccountVerifiedDialogState {
  /** "Continue and Apply" instead of "Continue" — Flutter profileInput.clickedOn == 'APPLIED' */
  showContinueApply: boolean;
}

export interface SignUpState {
  phase: SignUpPhase;
  isLoading: boolean;
  /** Flutter's message for the failed step (untranslated) */
  error: string;
  isEmailDuplicate: boolean;
  isMobileDuplicate: boolean;
  disableResendEmail: boolean;
  registeredEmail: string;
  warningWithSupport: boolean;
  warningMessage: string;
  accountVerifiedDialog: AccountVerifiedDialogState | null;

  handleSignUp: (formData: SignUpFormData) => Promise<void>;
  handleResendVerification: () => Promise<void>;
  handleReSignUp: () => Promise<void>;
  handleLogout: () => Promise<void>;
  handleAccountVerifiedDialogClose: () => void;
  clearError: () => void;
  clearDuplicate: (field: 'email' | 'mobile') => void;
  onResendTimerEnd: () => void;
}

export function useSignup(): SignUpState {
  const navigate = useNavigate();
  const location = useLocation();
  const { content } = useTenantStore();
  const { user, setMfaVerified } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(false);
  const [isMobileDuplicate, setIsMobileDuplicate] = useState(false);
  const [disableResendEmail, setDisableResendEmail] = useState(true);
  const [warningWithSupport, setWarningWithSupport] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [accountVerifiedDialog, setAccountVerifiedDialog] = useState<AccountVerifiedDialogState | null>(null);
  /**
   * Set while the page waits for the email to be verified, and kept after that until the page
   * is left. user.reload() fires onIdTokenChanged, so once the email is verified the store says
   * isAuthorized — without this the card would turn back into the form and AuthPage would
   * redirect to /home before the verified dialog and its navigation.
   */
  const [isVerificationFlow, setIsVerificationFlow] = useState(false);

  const emailVerifyTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** uid the verification email was already sent for during this visit */
  const emailSentForUidRef = useRef<string | null>(null);
  /** handleSignUp sends the email itself, after the candidate record is saved */
  const signUpInProgressRef = useRef(false);

  const isUnverified = !!user && !user.emailVerified;
  const phase: SignUpPhase = !!user && (isUnverified || isVerificationFlow) ? 'verifyEmail' : 'form';
  const registeredEmail = phase === 'verifyEmail' ? user?.email ?? '' : '';

  const stopPolling = useCallback(() => {
    if (emailVerifyTimerRef.current) {
      clearInterval(emailVerifyTimerRef.current);
      emailVerifyTimerRef.current = null;
    }
  }, []);

  // Cleanup polling timer on unmount
  useEffect(() => stopPolling, [stopPolling]);

  const clearError = useCallback(() => {
    setError('');
    setWarningWithSupport(false);
    setWarningMessage('');
  }, []);

  /** Flutter clears the duplicate flag as soon as the field is edited. */
  const clearDuplicate = useCallback((field: 'email' | 'mobile') => {
    if (field === 'email') setIsEmailDuplicate(false);
    else setIsMobileDuplicate(false);
  }, []);

  /**
   * Email verified: load the candidate, then show the verified dialog.
   * Mirrors Flutter WebSignupBloc._CancleTimer → _OnGetUserDetails and the WebSignupPage listener.
   */
  const handleEmailVerified = useCallback(async () => {
    try {
      await getUserDetails();
    } catch (detailsError) {
      if (isHandledByErrorInterceptor(detailsError)) return;
      // Flutter stops here and the dialog never shows; React carries on, as after login.
    }
    const returnState = (location.state ?? {}) as LoginRedirectState;
    setAccountVerifiedDialog({ showContinueApply: returnState.clickedOn === 'APPLIED' });
  }, [location.state]);

  /**
   * The verified dialog closed (button or outside tap): tenant MFA → /mfa, otherwise the
   * ProfileInput destination. Mirrors the showDialog(...).then() in WebSignupPage.
   */
  const handleAccountVerifiedDialogClose = useCallback(() => {
    const returnState = (location.state ?? {}) as LoginRedirectState;
    // isVerificationFlow stays set: the page is being left
    setAccountVerifiedDialog(null);

    if (content?.isMfaEnabled ?? false) {
      void analyticsService.logEvent('signup_page', 'for_mfa_verification');
      void analyticsService.logScreenView({ screenName: ROUTES.MFA });
      navigate(ROUTES.MFA, {
        state: { ...returnState, email: auth.currentUser?.email ?? '' },
      });
    } else {
      const redirect = resolvePostLoginRedirect(returnState);
      void analyticsService.logScreenView({ screenName: redirect.screenName, screenClass: redirect.screenClass });
      navigate(redirect.path, { replace: true, state: redirect.state });
    }

    // Refresh the ID token so API calls carry email_verified=true; the
    // resulting onIdTokenChanged also refreshes isAuthorized in the store.
    auth.currentUser?.getIdToken(true).catch(() => {/* next call refreshes it */});
  }, [content, navigate, location.state]);

  /**
   * Polls Firebase every 5 seconds to check if email has been verified.
   * Mirrors Flutter WebSignupBloc._CheckEmailVerified + _CancleTimer.
   */
  const startEmailVerificationPolling = useCallback(() => {
    stopPolling();
    setIsVerificationFlow(true);

    emailVerifyTimerRef.current = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        // User is null — stop polling, navigate to home (Flutter goToHome)
        stopPolling();
        setIsVerificationFlow(false);
        void analyticsService.logScreenView({ screenName: ROUTES.HOME });
        navigate(ROUTES.HOME, { replace: true });
        return;
      }

      try {
        await currentUser.reload();
      } catch {
        return; // network hiccup — try again on the next tick
      }
      if (currentUser.emailVerified) {
        stopPolling();
        handleEmailVerified();
      }
    }, 5000);
  }, [navigate, stopPolling, handleEmailVerified]);

  /**
   * Send the verification email and poll only if it was sent.
   * Mirrors Flutter WebSignupBloc._SignupEmailVerify.
   */
  const sendVerificationEmail = useCallback(async (email: string): Promise<boolean> => {
    emailSentForUidRef.current = auth.currentUser?.uid ?? null;
    setDisableResendEmail(true);
    const sent = await accountVerifyEmail(email);
    if (sent) startEmailVerificationPolling();
    return sent;
  }, [startEmailVerificationPolling]);

  // Signed in but not verified (login, refresh, redirect): send the email once per visit
  useEffect(() => {
    if (!isUnverified || !user?.email || signUpInProgressRef.current) return;
    if (emailSentForUidRef.current === user.uid) return;
    sendVerificationEmail(user.email);
  }, [isUnverified, user, sendVerificationEmail]);

  /**
   * Main sign up handler.
   * Mirrors Flutter WebSignupBloc._CheckDuplicateUser → _OnSignupWithEmail.
   */
  const handleSignUp = useCallback(async (formData: SignUpFormData) => {
    setError('');
    setIsEmailDuplicate(false);
    setIsMobileDuplicate(false);
    setWarningWithSupport(false);
    setWarningMessage('');
    setIsLoading(true);
    signUpInProgressRef.current = true;

    try {
      // Step 1: Check duplicate user
      const email = formData.email.trim().toLowerCase();
      const phone = formData.phone.trim();

      const duplicateCheck = await checkDuplicateUser({
        emailId: email || undefined,
        mobileNumber: phone || undefined,
      });

      if (!duplicateCheck.success) {
        setError(duplicateCheck.message);
        return;
      }

      const duplicateResult = duplicateCheck.result;
      if (duplicateResult.isDuplicate) {
        // Flutter ErrorConstants.emailMobileDuplicate / emailDuplicate / mobileDuplicate
        let message = '';
        if (duplicateResult.isByEmail && duplicateResult.isByPhone) {
          message = 'Both email and mobile number already exist. Please use different credentials.';
        } else if (duplicateResult.isByEmail) {
          message = 'Email already exists. Please use a different email address.';
        } else if (duplicateResult.isByPhone) {
          message = 'Mobile number already exists. Please use a different mobile number.';
        }

        setIsEmailDuplicate(duplicateResult.isByEmail);
        setIsMobileDuplicate(duplicateResult.isByPhone);
        setError(message);
        return;
      }

      // Step 2: Firebase createUserWithEmailAndPassword — the user is now signed in
      // but unverified, so the card switches to the verify-email state
      setMfaVerified(false);

      const signUpResult = await signUpWithEmail(email, formData.password);
      void analyticsService.logEvent('EMAIL_SIGN_UP', 'Email Signup');

      if (!signUpResult.success) {
        if (signUpResult.code === 'password-does-not-meet-requirements') {
          // Flutter shows Firebase's message in the error-and-support dialog
          setWarningWithSupport(true);
          setWarningMessage(signUpResult.message);
        }
        setError(signUpResult.message);
        return;
      }

      // Step 3: Store name/phone in localStorage
      // Mirrors Flutter SharedPreferences usage
      localStorage.setItem('name', `${formData.firstName} ${formData.lastName}`);
      localStorage.setItem('phone', phone);
      localStorage.setItem('countryCode', formData.countryCode);
      localStorage.setItem('userEmailId', email);

      // Step 4: Update user details on backend — CandidateDetails.fromJson(newDataJson).toJson()
      try {
        await updateUserDetails(candidateDetailsToJson(candidateDetailsFromJson({
          firstName: formData.firstName,
          lastName: formData.lastName,
          primaryContactNumberCountryCode: formData.countryCode,
          primaryContactNumber: phone,
          primaryEmailId: email,
        })));
      } catch (updateError) {
        if (isHandledByErrorInterceptor(updateError)) return;
        // Flutter ignores a failed update
      }
      void analyticsService.logEvent('signup_page', 'signup_success_going_for_verification', `email: ${email}`);

      // Step 5: Send verification email, then poll once it is sent
      await sendVerificationEmail(email);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      signUpInProgressRef.current = false;
      setIsLoading(false);
    }
  }, [sendVerificationEmail, setMfaVerified]);

  /**
   * Resend verification email.
   * Mirrors Flutter WebSignupBloc._SignupEmailVerify (a failed send shows nothing).
   */
  const handleResendVerification = useCallback(async () => {
    if (!registeredEmail) return;

    setIsLoading(true);
    try {
      if (await sendVerificationEmail(registeredEmail)) {
        setError('');
      }
    } finally {
      setIsLoading(false);
    }
  }, [registeredEmail, sendVerificationEmail]);

  /**
   * Re-sign up: logout and show the sign up form again on /signUp.
   * Mirrors Flutter WebSignupBloc._OnResignup + the listener's navigateTo(signUpRoute).
   */
  const handleReSignUp = useCallback(async () => {
    setIsLoading(true);
    stopPolling();

    try {
      await logoutUser();
    } catch {
      // continue
    }

    emailSentForUidRef.current = null;
    setIsVerificationFlow(false);
    setError('');
    setIsLoading(false);
    void analyticsService.logScreenView({ screenName: ROUTES.SIGN_UP });
    navigate(ROUTES.SIGN_UP);
  }, [navigate, stopPolling]);

  /**
   * Logout from sign up page.
   * Mirrors Flutter WebSignupBloc._OnLogout + the listener's navigateTo(homeRoute).
   */
  const handleLogout = useCallback(async () => {
    setIsLoading(true);
    stopPolling();

    try {
      await logoutUser();
    } catch {
      // continue
    }

    emailSentForUidRef.current = null;
    setIsVerificationFlow(false);
    setIsLoading(false);
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    navigate(ROUTES.HOME, { replace: true });
  }, [navigate, stopPolling]);

  /**
   * Called when the resend countdown timer ends.
   * Mirrors Flutter VerifyAccCard onEnd callback.
   */
  const onResendTimerEnd = useCallback(() => {
    setDisableResendEmail(false);
  }, []);

  return {
    phase,
    isLoading,
    error,
    isEmailDuplicate,
    isMobileDuplicate,
    disableResendEmail,
    registeredEmail,
    warningWithSupport,
    warningMessage,
    accountVerifiedDialog,
    handleSignUp,
    handleResendVerification,
    handleReSignUp,
    handleLogout,
    handleAccountVerifiedDialogClose,
    clearError,
    clearDuplicate,
    onResendTimerEnd,
  };
}
